export type DotPhraseTrigger = 'mypubs' | 'mytrials';

export type DotPhrase = {
  trigger: DotPhraseTrigger;
  label: string;
  description: string;
  /**
   * Whether the trigger typed without its dot is offered as the shortcut.
   * Leave it off for a trigger that is an ordinary word at the start of a
   * sentence, where the offer would pop up over plain prose. Action phrases
   * never set it, so "Draft …" in prose never runs one.
   */
  matchesUndotted: boolean;
};

/**
 * Composer dot phrases, modeled on Epic SmartPhrases: typing `.` plus a
 * trigger opens a command that expands into the draft. Each cites from the
 * expert's own profile, so the menu offers the registry to experts only. Add
 * new phrases here.
 */
export const DOT_PHRASES: DotPhrase[] = [
  {
    trigger: 'mypubs',
    label: '.mypubs',
    description: 'Cite one of your publications',
    matchesUndotted: true,
  },
  {
    trigger: 'mytrials',
    label: '.mytrials',
    description: 'Cite one of your clinical trials',
    matchesUndotted: true,
  },
];

/**
 * A phrase that does something rather than expanding into a citation. Picking it
 * removes the token from the draft and runs `onRun` with the position the
 * token began at, so whatever it opens can put its result back there. Another
 * feature supplies it at runtime, which is why it is not in the registry: the
 * menu never learns what an action does or when it applies.
 */
export type ActionDotPhrase = {
  trigger: string;
  label: string;
  description: string;
  onRun: (start: number) => void;
};

/** What parsing needs of a phrase, registry or action. */
type ParsablePhrase = { trigger: string; matchesUndotted?: boolean };

/**
 * Appended to the composer placeholder while dot phrases are on. Without it an
 * expert has no way to learn the shortcut exists; nothing else on screen hints
 * at it.
 */
export const DOT_PHRASE_PLACEHOLDER_HINT = 'type .mypubs to cite a paper';

/**
 * The composer placeholder with a shortcut hint, kept to one line on a phone.
 *
 * The plain placeholder becomes "Reply, or …", which still says the box is
 * where a reply goes while leaving room to say what the shortcut does. A
 * placeholder that names who the message is sent as keeps that and takes the
 * hint after it, since the sender matters more than the shortcut. The caller
 * says which it has, rather than this matching the placeholder's wording.
 */
export function withDotPhraseHint(
  placeholder: string,
  {
    hint = DOT_PHRASE_PLACEHOLDER_HINT,
    namesSender,
  }: {
    hint?: string;
    namesSender: boolean;
  }
): string {
  if (!namesSender) {
    return `Reply, or ${hint}`;
  }

  return `${placeholder} — ${hint}`;
}

export type ParsedDotPhrase<Phrase extends ParsablePhrase = DotPhrase> =
  | { kind: 'partial'; start: number; typed: string; matches: Phrase[] }
  | { kind: 'command'; start: number; phrase: Phrase; query: string }
  | { kind: 'undotted'; start: number; phrase: Phrase; query: string }
  | { kind: 'unmatched'; start: number; typed: string };

const DOT_TOKEN = /(^|\s)\.([a-z]+)(?= |$)/gi;

/**
 * A trigger word typed without its dot, the way the shortcut sounds when
 * someone is told it aloud. It counts only where a sentence or line starts and
 * only when a query follows, so prose such as "I checked mypubs for it" never
 * matches.
 */
const UNDOTTED_TOKEN = /(^|[.!?] +)([a-z]+) +(?=\S)/gi;

/**
 * The trigger as typed, or with its first letter capitalized, which is what a
 * phone keyboard produces at the start of a sentence. The brand spelling
 * "PubMed" is left alone: it is how someone writes about the site, not how
 * someone types a shortcut.
 */
function spellsTrigger(word: string, trigger: string): boolean {
  return word === trigger || word === trigger.charAt(0).toUpperCase() + trigger.slice(1);
}

/**
 * Finds a dot phrase on the draft's last line. The `.` must open the line or
 * follow whitespace, so `e.g.` and URLs never trigger it, and at least one
 * letter must follow it, so a stray ` .` at a line's end never takes over
 * Enter. Returns `partial`
 * while the trigger is still being typed and `command` once a known trigger is
 * followed by a space. `phrases` narrows the registry to what this user may
 * use, so an unavailable phrase never opens a menu.
 *
 * Tokens are tried right to left and the first one that parses wins. Taking
 * the leftmost instead let an earlier dot-word — an unknown `.net`, or a phrase
 * this user cannot use — hide a phrase typed after it on the same line.
 *
 * With no dotted phrase, a trigger word typed without its dot and followed by
 * a query returns `undotted`, so the menu can offer the shortcut. The owner
 * treats that as a suggestion only: it must not take over Enter, because the
 * expert may simply be writing.
 *
 * Last, a dot-word at the end of the line that names no phrase returns
 * `unmatched`, so a typo such as `.pubed` still shows the phrases that exist.
 * It is the weakest signal — `.NET` ends a sentence just as easily — so it
 * loses to every other reading, and the owner keeps it passive like
 * `undotted`.
 */
export function parseTrailingDotPhrase<Phrase extends ParsablePhrase = DotPhrase>(
  text: string,
  phrases: Phrase[] = DOT_PHRASES as unknown as Phrase[]
): ParsedDotPhrase<Phrase> | null {
  const lineStart = text.lastIndexOf('\n') + 1;

  const line = text.slice(lineStart);

  const tokens = [...line.matchAll(DOT_TOKEN)].reverse();

  let unmatched: ParsedDotPhrase<Phrase> | null = null;

  for (const token of tokens) {
    const parsed = parseToken(line, token, phrases);

    if (parsed?.kind === 'unmatched') {
      unmatched = { ...parsed, start: parsed.start + lineStart };
    } else if (parsed) {
      return { ...parsed, start: parsed.start + lineStart };
    }
  }

  const sentenceStarts = [...line.matchAll(UNDOTTED_TOKEN)].reverse();

  for (const sentenceStart of sentenceStarts) {
    const [whole, leading, word] = sentenceStart;

    const phrase = phrases.find(
      (candidate) => candidate.matchesUndotted && spellsTrigger(word, candidate.trigger)
    );

    if (phrase) {
      const index = sentenceStart.index ?? 0;

      return {
        kind: 'undotted',
        start: lineStart + index + leading.length,
        phrase,
        query: line.slice(index + whole.length).trim(),
      };
    }
  }

  return unmatched;
}

function parseToken<Phrase extends ParsablePhrase>(
  line: string,
  token: RegExpMatchArray,
  phrases: Phrase[]
): ParsedDotPhrase<Phrase> | null {
  const [whole, leading, typed] = token;

  const start = (token.index ?? 0) + leading.length;

  const rest = line.slice((token.index ?? 0) + whole.length);

  const typedLower = typed.toLowerCase();

  if (rest === '') {
    const matches = phrases.filter((phrase) => phrase.trigger.startsWith(typedLower));

    return matches.length === 0
      ? { kind: 'unmatched', start, typed }
      : { kind: 'partial', start, typed, matches };
  }

  const phrase = phrases.find((candidate) => candidate.trigger === typedLower);

  return phrase ? { kind: 'command', start, phrase, query: rest.trim() } : null;
}

/** Replaces the trailing token with the completed trigger, ready for a query. */
export function completeDotPhrase(
  text: string,
  start: number,
  phrase: Pick<DotPhrase, 'trigger'>
): string {
  return `${text.slice(0, start)}.${phrase.trigger} `;
}

/**
 * Takes an action phrase's token out of the draft and nothing else. Words typed
 * after it are the expert's own, not a query, so they stay where they were.
 */
export function removeActionToken(text: string, start: number): string {
  return text.slice(0, start) + text.slice(start).replace(/^\.[a-z]+ ?/i, '');
}

/** Puts the missing dot on an undotted trigger, keeping the query typed after it. */
export function addMissingDot(
  text: string,
  start: number,
  phrase: Pick<DotPhrase, 'trigger'>,
  query: string
): string {
  return `${text.slice(0, start)}.${phrase.trigger} ${query}`;
}
